import { useCallback, useState, useRef } from 'react'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, uniqBy, omit } from 'lodash'
import { v4 as uuid } from 'uuid'

import Modal from 'components/Modal'
import Info from 'components/RevenueCenters/Form/Info'
import { SpySidebar } from 'components/shared/SpySidebar'
import BusinessEndTime from 'components/RevenueCenters/Form/BusinessEndTime'
import Merchant from 'components/RevenueCenters/Form/Merchant'
import ServiceTypes from 'components/RevenueCenters/Form/ServiceTypes'
import FilterData from 'components/RevenueCenters/Form/FilterData'
import AddServiceType from 'components/RevenueCenters/Form/AddServiceType'
import Items from 'components/RevenueCenters/Form/Drawers/Items'
import MenuSettings from 'components/RevenueCenters/Form/MenuSettings'

import {
  clearLocationSettings,
  clearMenuSettings,
  clearServiceTypes,
  clearServiceTypesWithItems,
  createSettings,
} from 'store/RevenueCenters/actions'
import {
  clearEditRevenueCenter,
  createRevenueCenter,
  updateRevenueCenter,
} from 'store/RevenueCenters/actions'
import { getCurrentVenueUuid } from 'store/Venues/selector'
import {
  getAllMenuSettings,
  getCurrentRevenueCenter,
  getEditedFieldValuesRevenueCenter,
  getFilteredDataForRevenueCenter,
  getLocationSettings,
  getMenusForRevenueCenter,
  getServiceTypesWithItems,
} from 'store/RevenueCenters/selector'
import { getItems } from 'store/Items/selector'
import { getServiceTypes } from 'store/RevenueCenters/selector'
import { createMenus, updateMenus } from 'store/Menus/actions'
import { updateGlobalItem } from 'store/Items/actions'
import { getOrgName } from 'store/AppState/selector'
import { compareArrays } from 'utils/revenueCenters/compareArrays'
import { extendMenusByHoursValues } from 'utils/extendMenusByHoursValues'
import { transformedItemsToRevenueCenter } from 'utils/revenueCenters/transformedItemsToRevenueCenter'
import { menuSettingsDataWithoutKeyUuid } from 'utils/menuSettingsDataWithoutKeyUuid'
import { transformMenuSettings } from 'utils/revenueCenters/transformMenuSettings'

const RevenueCentersForm = ({ isLoading }) => {
  const [showValidationErrors, setShowValidationErrors] =
    useState(false)

  const { state: locationState } = useLocation()

  const modalType = locationState?.modalType
  const revenueCenterUuid = locationState?.revenueCenterUuid

  const history = useHistory()
  const dispatch = useDispatch()

  const itemSettingsRef = useRef()

  const orgName = useSelector(getOrgName)
  const venueUuid = useSelector(getCurrentVenueUuid)
  const currentRevenueCenter = useSelector((state) =>
    getCurrentRevenueCenter(state, revenueCenterUuid)
  )
  const allItems = useSelector(getItems)
  const allMenusForRevenueCenter = useSelector((state) =>
    getServiceTypes(state)
  )
  const oldMenusForRevenueCenter = useSelector((state) =>
    getMenusForRevenueCenter(state, currentRevenueCenter.id)
  )
  const editedFieldValues = useSelector(
    getEditedFieldValuesRevenueCenter
  )
  const serviceTypesWithItems = useSelector(getServiceTypesWithItems)

  const currentFilterData = useSelector((state) =>
    getFilteredDataForRevenueCenter(state, revenueCenterUuid)
  )
  const storedLocationSettings = useSelector(getLocationSettings)

  const menuSettingsData = useSelector(getAllMenuSettings)

  const { provideTipSuggestions, androidPayMerchantId } = isEmpty(
    storedLocationSettings
  )
    ? currentRevenueCenter
    : storedLocationSettings

  const { name, externalRefId, description, localTime } = isEmpty(
    editedFieldValues
  )
    ? currentRevenueCenter
    : editedFieldValues

  const filterData = isEmpty(editedFieldValues?.filterData)
    ? currentFilterData
    : editedFieldValues?.filterData

  const businessEndTime = isEmpty(editedFieldValues?.businessEndTime)
    ? currentRevenueCenter?.businessEndTime
    : editedFieldValues?.businessEndTime

  const merchantId = isEmpty(editedFieldValues?.merchantId)
    ? currentRevenueCenter?.merchantId
    : editedFieldValues?.merchantId

  const isNewRevenueCenter = revenueCenterUuid === 'new'

  const initialValues = {
    name,
    externalRefId,
    description,
    localTime,
    businessEndTime,
    hours: '',
    minutes: '',
    filterData,
    merchantId,
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    localTime: Yup.string().required('Required'),
    time: Yup.string()
      .matches(
        /^([01][0-9]|2[0-3]):([0-5][0-9])$/gm,
        'Invalid time format'
      )
      .required('Required'),
  })

  const sectionRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ]

  const settings = [
    {
      Component: Info,
      ref: sectionRefs[0],
      text: 'info',
      url: '#info',
      revenueCenterUuid: currentRevenueCenter?.uuid,
    },
    {
      Component: BusinessEndTime,
      ref: sectionRefs[1],
      text: 'Business End Time',
      url: '#businessEndTime',
    },
    {
      Component: Merchant,
      ref: sectionRefs[2],
      text: 'Revenue Center Merchant',
      url: '#merchant',
    },
    {
      Component: ServiceTypes,
      ref: sectionRefs[3],
      text: 'Service Types',
      url: '#serviceTypes',
    },
    {
      Component: FilterData,
      ref: sectionRefs[4],
      text: 'Filter Data',
      url: '#filterData',
    },
    // Commented for future using. Reason: endpoint `/external/settings` used in Location Settings does not accurately represent the configured value, ticket: T29403.
    // {
    //   Component: LocationSettingsSection,
    //   ref: sectionRefs[5],
    //   text: 'Settings',
    //   url: '#settings',
    // },
  ]

  const disableUpdateButton = useCallback(
    (dirty, errors) => {
      const disableButton =
        !dirty || (showValidationErrors && !isEmpty(errors))

      return disableButton
    },
    [showValidationErrors]
  )

  const onClickSaveButton = () => {
    setShowValidationErrors(true)
  }

  const onCloseModal = () => {
    history.push({ state: { revenueCenterUuid: null } })
    dispatch(clearServiceTypes())
    dispatch(clearLocationSettings())
    dispatch(clearMenuSettings())
    dispatch(clearEditRevenueCenter())
    dispatch(clearServiceTypesWithItems())
  }

  const modalTitle = isNewRevenueCenter
    ? 'Add a Revenue Center'
    : 'Edit Revenue Center'

  const onFormKeyDown = (event) => {
    if ((event.charCode || event.keyCode) === 13) {
      event.preventDefault()
    }
  }

  const onSubmit = ({
    name,
    externalRefId,
    description,
    localTime,
    filterData,
    businessEndTime,
    merchantId,
  }) => {
    const businessEndTimeData = isEmpty(businessEndTime)
      ? null
      : businessEndTime

    const newRevenueCenterParams = [
      {
        name,
        externalRefId,
        description,
        localTime,
        filterData: filterData,
        businessEndTimeData,
        venueUuid,
        uuid: uuid(),
        organization_name: orgName,
        merchantId,
      },
    ]

    const newRevenueCenterParamsWithLocationSettings = [
      {
        name,
        externalRefId,
        description,
        localTime,
        filterData,
        businessEndTimeData,
        venueUuid,
        uuid: uuid(),
        organization_name: orgName,
        provideTipSuggestions,
        androidPayMerchantId,
        merchantId,
      },
    ]

    const newRevenueCenterParamsToSend = !isEmpty(
      storedLocationSettings
    )
      ? newRevenueCenterParamsWithLocationSettings
      : newRevenueCenterParams

    const updateRevenueCenterParams = [
      {
        ...currentRevenueCenter,
        name,
        externalRefId,
        description,
        localTime,
        filterData: filterData,
        businessEndTime,
        merchantId,
      },
    ]

    const updateRevenueCenterParamsWithLocationSettings = [
      {
        ...currentRevenueCenter,
        name,
        externalRefId,
        description,
        localTime,
        filterData: filterData,
        businessEndTime,
        androidPayMerchantId,
        merchantId,
      },
    ]

    const updateRevenueCenterParamsToSend = !isEmpty(
      storedLocationSettings
    )
      ? updateRevenueCenterParamsWithLocationSettings
      : updateRevenueCenterParams

    const extendedMenusWithStoredOrderAheadHoursAndBusinessHours =
      extendMenusByHoursValues(
        allMenusForRevenueCenter,
        menuSettingsData
      )

    // this function Compare created Service Types and existed Service types in order to send changed or new serviceTypes
    const { changedElements, newElements } = compareArrays(
      allMenusForRevenueCenter,
      oldMenusForRevenueCenter,
      'displayName'
    )

    // Those are new serviceTypes (menus) extended of orderAhead and opearting hours (business)
    const newElementsToSend = uniqBy(
      [
        ...extendedMenusWithStoredOrderAheadHoursAndBusinessHours,
        ...newElements,
      ],
      'uuid'
    )

    // Those are changed serviceTypes (menus) extended of orderAhead and opearting hours (business)
    const changeElemensToSend = uniqBy(
      [
        ...extendedMenusWithStoredOrderAheadHoursAndBusinessHours,
        ...changedElements,
      ],
      'uuid'
    )

    // In this function omit business hours to send, because we can't send businessHours and operatingHours in one request
    const changeElementsToSendWithoutBusinessHours =
      changeElemensToSend.map((element) =>
        omit(element, 'businessHours')
      )

    const transformedItems = transformedItemsToRevenueCenter(
      allItems,
      // serviceTypesWithItems = object of arrays of items, where index of each array is menu uuid
      serviceTypesWithItems
    )

    // menuSettingsDataWithoutUuid function which return menus settings without uuid key, which is only necessery in redux store
    const menuSettingsDataWithoutUuid =
      menuSettingsDataWithoutKeyUuid(menuSettingsData)

    const locationSettingsFields = {
      provideTipSuggestions:
        storedLocationSettings?.provideTipSuggestions ?? '',
    }

    // Commented for future using. Reason: endpoint `/external/settings` used in Location Settings does not accurately represent the configured value, ticket: T29403.
    // const transformedLocationSettingsDataToSend =
    //   transformLocationSettings(
    //     locationSettingsFields,
    //     venueUuid,
    //     revenueCenterUuid
    //   )

    const transformMenuSettingsToSend = transformMenuSettings(
      menuSettingsDataWithoutUuid,
      venueUuid,
      revenueCenterUuid
    )

    const settingsDataToSend = [
      // Commented for future using. Reason: endpoint `/external/settings` used in Location Settings does not accurately represent the configured value, ticket: T29403.

      // ...transformedLocationSettingsDataToSend,
      ...transformMenuSettingsToSend,
    ]

    if (isNewRevenueCenter) {
      dispatch(createRevenueCenter(newRevenueCenterParamsToSend))
        .then((response) => {
          if (!isEmpty(newElementsToSend)) {
            const newElementsWithStandId = newElementsToSend.reduce(
              (res, cur) => {
                if (isEmpty(cur?.standId)) {
                  return [
                    ...res,
                    { ...cur, standId: response.payload[0].id },
                  ]
                } else {
                  return res
                }
              },
              []
            )
            dispatch(createMenus(newElementsWithStandId))
          }
          return response
        })
        .then((response) => {
          if (!isEmpty(transformedItems)) {
            dispatch(updateGlobalItem(transformedItems))
          }
          return response
        })
        .then((response) => {
          // Commented for future using. Reason: endpoint `/external/settings` used in Location Settings does not accurately represent the configured value, ticket: T29403.

          // const newLocationSettingsToSend = transformMenuSettings(
          //   menuSettingsDataWithoutUuid,
          //   venueUuid,
          //   response.payload[0].uuid
          // )

          const newMenuSettingsToSend = transformMenuSettings(
            menuSettingsDataWithoutUuid,
            venueUuid,
            response.payload[0].uuid
          )

          const newSettingsToSend = [
            // Commented for future using. Reason: endpoint `/external/settings` used in Location Settings does not accurately represent the configured value, ticket: T29403.

            // ...newLocationSettingsToSend,
            ...newMenuSettingsToSend,
          ]

          if (!isEmpty(newSettingsToSend)) {
            dispatch(createSettings(newSettingsToSend))
          }
        })
        .then(() => {
          dispatch(clearServiceTypes())
          dispatch(clearLocationSettings())
          dispatch(clearEditRevenueCenter())
          history.push({ state: { revenueCenterUuid: null } })
        })
    } else {
      const revenueCenterToUpdate = dispatch(
        updateRevenueCenter(updateRevenueCenterParamsToSend)
      )

      const menusToUpdate = !isEmpty(
        changeElementsToSendWithoutBusinessHours
      )
        ? dispatch(
            updateMenus(changeElementsToSendWithoutBusinessHours)
          )
        : Promise.resolve()

      const menusToCreate = !isEmpty(newElements)
        ? dispatch(createMenus(newElementsToSend))
        : Promise.resolve()

      const globalItemToUpdate = !isEmpty(transformedItems)
        ? dispatch(updateGlobalItem(transformedItems))
        : Promise.resolve()

      const settingsToCreate = !isEmpty(settingsDataToSend)
        ? dispatch(createSettings(settingsDataToSend))
        : Promise.resolve()

      Promise.all([
        revenueCenterToUpdate,
        menusToUpdate,
        menusToCreate,
        globalItemToUpdate,
        settingsToCreate,
      ]).then(() => {
        dispatch(clearServiceTypes())
        dispatch(clearLocationSettings())
        dispatch(clearEditRevenueCenter())
        history.push({ state: { revenueCenterUuid: null } })
      })
    }
  }

  return (
    <>
      {isEmpty(modalType) && (
        <Modal
          title={modalTitle}
          onClose={onCloseModal}
          onRequestClose={onCloseModal}
          isLoading={isLoading}
        >
          <div className="modal-body">
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={onSubmit}
              validationSchema={validationSchema}
            >
              {({ dirty, errors }) => (
                <Form onKeyDown={onFormKeyDown}>
                  <div
                    className="item-settings"
                    ref={itemSettingsRef}
                  >
                    {settings.map(
                      ({ Component, ref, text, ...props }) => (
                        <section
                          key={text}
                          className="item-settings__section"
                          ref={ref}
                          id={text}
                        >
                          <Component
                            {...props}
                            key={text}
                            title={text}
                            showValidationErrors={
                              showValidationErrors
                            }
                          />
                        </section>
                      )
                    )}
                    <div className="btn-wrapper">
                      <button
                        className="btn -update"
                        onClick={onClickSaveButton}
                        disabled={disableUpdateButton(dirty, errors)}
                        type="submit"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
            <SpySidebar
              settings={settings}
              root={itemSettingsRef?.current}
            />
          </div>
        </Modal>
      )}

      {modalType === 'service-type' && <AddServiceType />}
      {modalType === 'items' && <Items />}
      {modalType === 'menu-settings' && <MenuSettings />}
    </>
  )
}

export default RevenueCentersForm
